import React, { useState, useEffect, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, sp, rad, type, icBox, listRow } from "../../constants/styles";
import Ic from "../common/Ic";
import { formatPlanLabel, formatPrice } from "../../utils/plans";

const TAB_KEYS = ["overview", "properties", "tenants", "service", "billing", "audit"];
const TAB_LABELS = { overview: "Overview", properties: "Properties", tenants: "Tenants", service: "Service", billing: "Billing", audit: "Audit" };

export default function AdminOrgDetail() {
  const { t, h, go, selOrgId, flash } = useContext(Ctx);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [limitInput, setLimitInput] = useState("");
  const [tab, setTab] = useState("overview");
  const [allPlans, setAllPlans] = useState([]);
  const [ppRate, setPpRate] = useState(null); // per-property base monthly unit rate (paise)

  useEffect(() => {
    const load = async () => {
      try {
        const [orgRes, plansRes, pubPlansRes] = await Promise.all([api.adminGetOrg(selOrgId), api.adminGetPlans(), api.getPlans()]);
        if (orgRes.success && orgRes.org) {
          setOrg(orgRes.org);
          setLimitInput(orgRes.org.plan_limit_properties?.toString() || "3");
        }
        if (plansRes.success) setAllPlans(plansRes.plans);
        // Per-property plan lives in a separate table; grab its base unit rate
        // from the public plans endpoint so Plan Control can show "₹X/property".
        const pp = pubPlansRes && pubPlansRes.plans && pubPlansRes.plans.find(p => p.pricing_model === "per_property");
        if (pp && pp.pp_unit_price_monthly != null) setPpRate(pp.pp_unit_price_monthly);
      } catch (e) {
        console.error("Admin org detail error:", e);
      } finally {
        setLoading(false);
      }
    };
    if (selOrgId) load();
  }, [selOrgId]);

  const updatePlan = async (newPlan) => {
    setSaving(true);
    // Get limit from plan definition
    const planDef = allPlans.find(p => p.id === newPlan);
    // Per-property is usage-based (pay per property) → no fixed cap.
    const isPerProperty = String(newPlan).startsWith("per_property");
    const limit = isPerProperty ? 999999 : (planDef ? planDef.max_properties : (parseInt(limitInput) || 3));
    const res = await api.adminPatchOrg(selOrgId, { plan: newPlan, plan_limit_properties: limit });
    if (res.success && res.org) {
      setOrg(prev => ({ ...prev, ...res.org }));
      flash(t.planUpdated || "Plan updated!");
    }
    setSaving(false);
  };

  const updateLimit = async () => {
    const val = parseInt(limitInput);
    if (!val || val < 1) return;
    setSaving(true);
    const res = await api.adminPatchOrg(selOrgId, { plan_limit_properties: val });
    if (res.success && res.org) {
      setOrg(prev => ({ ...prev, ...res.org }));
      flash(t.planUpdated || "Limit updated!");
    }
    setSaving(false);
  };

  const toggleActive = async () => {
    setSaving(true);
    const newActive = org.active ? 0 : 1;
    const res = await api.adminPatchOrg(selOrgId, { active: newActive });
    if (res.success && res.org) {
      setOrg(prev => ({ ...prev, ...res.org }));
      flash(newActive ? (t.orgActive || "Activated") : (t.orgInactive || "Deactivated"));
    }
    setSaving(false);
  };

  if (loading) {
    return <div style={{ paddingTop: 40, textAlign: "center" }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
    </div>;
  }

  if (!org) {
    return <div style={{ paddingTop: sp.lg }}>
      <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.sm, display: "flex", alignItems: "center", gap: sp.xs }}>
        <Ic n="arrow-left" s={14} c={h.ac} /> Back
      </button>
      <div style={{ ...crd(h), textAlign: "center", padding: sp.xxxl }}>
        <div style={{ color: h.rd, ...type.body }}>Organization not found</div>
      </div>
    </div>;
  }

  const countItems = [
    { label: "Properties", value: org.prop_count, icon: "home", color: h.gn },
    { label: "Tenants", value: org.tenant_count, icon: "users", color: h.ac },
    { label: "Payments", value: org.payment_count, icon: "credit-card", color: h.wn },
    { label: "Service", value: org.service_count || 0, icon: "wrench", color: h.pp || h.ac },
    { label: "Leads", value: org.lead_count || 0, icon: "target", color: h.rd },
    { label: "Leases", value: org.lease_count, icon: "file-text", color: h.gn },
  ];

  const tabS = (active) => ({
    padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.pill, border: "none",
    background: active ? h.ac : "transparent", color: active ? "#fff" : h.ts,
    fontSize: 12, fontWeight: active ? 600 : 400, cursor: "pointer",
    transition: "all 0.2s",
  });

  const pillS = (active) => ({
    padding: `${sp.sm}px ${sp.xl}px`, borderRadius: rad.md, border: `2px solid ${active ? h.ac : h.bd}`,
    background: active ? `${h.ac}15` : "transparent", color: active ? h.ac : h.ts,
    cursor: saving ? "default" : "pointer", ...type.sm, fontWeight: active ? 700 : 400,
    opacity: saving ? 0.6 : 1,
  });

  return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
    {/* Back + Header */}
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
      <Ic n="arrow-left" s={14} c={h.ac} /> Back to Dashboard
    </button>

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: sp.lg }}>
      <div>
        <h2 style={{ ...type.h1, color: h.tx, margin: 0 }}>{org.name}</h2>
        <div style={{ ...type.xs, color: h.tt, marginTop: sp.xs }}>{org.slug} {"\u00B7"} {org.owner_phone || "No phone"}</div>
      </div>
      <div style={{ display: "flex", gap: sp.xs, flexWrap: "wrap", justifyContent: "flex-end" }}>
        {(() => { const planColors = { free: h.wn, starter: h.ac, growth: h.pp || "#8b5cf6", business: h.gn, pro: h.gn }; const pCol = planColors[org.plan] || h.ac; return <span style={bdg(`${pCol}20`, pCol)}>{formatPlanLabel(org.plan, org.plan_name)}</span>; })()}
        <span style={bdg(org.active ? `${h.gn}20` : `${h.rd}20`, org.active ? h.gn : h.rd)}>
          {org.active ? "Active" : "Inactive"}
        </span>
      </div>
    </div>

    {/* Plan Validity */}
    {org.plan && org.plan !== "free" && (org.plan_start || org.plan_end) && <div style={{ ...crd(h), padding: sp.md, marginBottom: sp.lg, background: `${h.ac}08`, border: `1px solid ${h.ac}20` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ ...type.xs, color: h.ts }}>Plan Validity</div>
          <div style={{ ...type.sm, color: h.tx, fontWeight: 500, marginTop: 2 }}>
            {org.plan_start ? new Date(org.plan_start).toLocaleDateString() : "—"} → {org.plan_end ? new Date(org.plan_end).toLocaleDateString() : "—"}
          </div>
        </div>
        {org.plan_end && <span style={bdg(
          new Date(org.plan_end) > new Date() ? `${h.gn}20` : `${h.rd}20`,
          new Date(org.plan_end) > new Date() ? h.gn : h.rd
        )}>{new Date(org.plan_end) > new Date() ? "Active" : "Expired"}</span>}
      </div>
    </div>}

    {/* Tabs */}
    <div style={{ display: "flex", gap: sp.xs, marginBottom: sp.lg, overflowX: "auto", paddingBottom: sp.xs }}>
      {TAB_KEYS.map(k => <button key={k} onClick={() => setTab(k)} style={tabS(tab === k)}>{TAB_LABELS[k]}</button>)}
    </div>

    {/* Overview Tab */}
    {tab === "overview" && <>
      {/* Stats Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: sp.sm, marginBottom: sp.lg }}>
        {countItems.map((item, i) => <div key={i} style={{ ...crd(h), textAlign: "center", padding: sp.md }}>
          <div style={icBox(item.color, 32)}>
            <Ic n={item.icon} s={14} c={item.color} />
          </div>
          <div style={{ ...type.h3, color: h.tx, marginTop: sp.xs }}>{item.value}</div>
          <div style={{ fontSize: 9, fontWeight: 500, color: h.tt, marginTop: 2 }}>{item.label}</div>
        </div>)}
      </div>

      {/* Org Info */}
      <div style={{ ...crd(h) }}>
        <div style={{ ...type.cap, color: h.ts, marginBottom: sp.md }}>Details</div>
        {[
          { label: "Owner Phone", value: org.owner_phone || "—" },
          { label: "Current Plan", value: formatPlanLabel(org.plan, org.plan_name) },
          { label: "Property Limit", value: (org.plan_limit_properties || 0) >= 999999 ? "Unlimited" : org.plan_limit_properties },
          { label: "Plan Status", value: org.plan_status || "active" },
          { label: "Plan Expires", value: org.plan_end ? new Date(org.plan_end).toLocaleDateString() : "N/A" },
          { label: "Created", value: org.created_at ? new Date(org.created_at).toLocaleDateString() : "—" },
        ].map((row, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", ...type.sm, padding: `${sp.sm}px 0`, borderBottom: i < 5 ? `1px solid ${h.bd}` : "none" }}>
          <span style={{ color: h.ts }}>{row.label}</span>
          <span style={{ color: h.tx, fontWeight: 500 }}>{row.value}</span>
        </div>)}
      </div>

      {/* Plan Control */}
      <div style={{ ...crd(h) }}>
        <div style={{ ...type.cap, color: h.ts, marginBottom: sp.md }}>Plan Control</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: sp.sm, marginBottom: sp.lg }}>
          {allPlans.length > 0 ? allPlans.map(p => (
            <button key={p.id} onClick={() => !saving && updatePlan(p.id)} style={pillS(org.plan === p.id)}>
              {p.name}
            </button>
          )) : <>
            <button onClick={() => !saving && updatePlan("free")} style={pillS(org.plan === "free")}>Free</button>
            <button onClick={() => !saving && updatePlan("pro")} style={pillS(org.plan === "pro")}>Pro</button>
          </>}
          {/* Per-property is a usage-based plan kept in a separate table, so it's
              not in allPlans (the fixed-tier list) — surface it here so an admin
              can assign it too. Highlighted for any per_property_* variant. */}
          <button onClick={() => !saving && updatePlan("per_property_v1")} style={pillS((org.plan || "").startsWith("per_property"))}>Per Property{ppRate != null ? ` · ${formatPrice(ppRate)}/prop` : ""}</button>
        </div>

        {(org.plan_limit_properties || 0) < 999999 && <div style={{ marginBottom: sp.md }}>
          <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.sm }}>Property Limit</label>
          <div style={{ display: "flex", gap: sp.sm }}>
            <input type="number" value={limitInput} onChange={e => setLimitInput(e.target.value)}
              style={{ flex: 1, padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, ...type.body, outline: "none" }}
              min="1" />
            <button onClick={updateLimit} disabled={saving}
              style={{ padding: `${sp.sm}px ${sp.lg}px`, borderRadius: rad.sm, border: "none", background: h.ac, color: "#fff", ...type.xs, cursor: "pointer", opacity: saving ? 0.6 : 1 }}
            >Save</button>
          </div>
        </div>}

        <div style={{ borderTop: `1px solid ${h.bd}`, paddingTop: sp.md, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ ...type.sm, color: h.tx, fontWeight: 500 }}>{org.active ? "Active" : "Inactive"}</div>
            <div style={{ fontSize: 10, color: h.tt }}>Toggle organization status</div>
          </div>
          <button onClick={toggleActive} disabled={saving}
            style={{ padding: `${sp.sm}px ${sp.lg}px`, borderRadius: rad.sm, border: "none",
              background: org.active ? `${h.rd}20` : `${h.gn}20`, color: org.active ? h.rd : h.gn,
              ...type.xs, cursor: "pointer", opacity: saving ? 0.6 : 1 }}
          >{org.active ? "Deactivate" : "Activate"}</button>
        </div>
      </div>
    </>}

    {/* Properties Tab */}
    {tab === "properties" && <div>
      {(!org.properties || org.properties.length === 0) ? (
        <div style={{ ...crd(h), textAlign: "center", padding: sp.xxxl }}>
          <Ic n="home" s={24} c={h.tt} />
          <div style={{ ...type.sm, color: h.tt, marginTop: sp.sm }}>No properties</div>
        </div>
      ) : (
        <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
          {org.properties.map((p, i) => (
            <div key={p.id} style={listRow(h, i === org.properties.length - 1)}>
              <div>
                <div style={{ ...type.sm, color: h.tx, fontWeight: 600 }}>{p.name}</div>
                <div style={{ ...type.xs, color: h.tt }}>{p.address || "No address"}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={bdg(p.type === "commercial" ? `${h.pp}20` : `${h.ac}20`, p.type === "commercial" ? h.pp : h.ac)}>
                  {p.type || "residential"}
                </span>
                {p.rent && <div style={{ ...type.xs, color: h.gn, marginTop: sp.xs }}>{"\u20B9"}{p.rent}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>}

    {/* Tenants Tab */}
    {tab === "tenants" && <div>
      {(!org.tenants || org.tenants.length === 0) ? (
        <div style={{ ...crd(h), textAlign: "center", padding: sp.xxxl }}>
          <Ic n="users" s={24} c={h.tt} />
          <div style={{ ...type.sm, color: h.tt, marginTop: sp.sm }}>No tenants</div>
        </div>
      ) : (
        <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
          {org.tenants.map((t, i) => (
            <div key={t.id} style={listRow(h, i === org.tenants.length - 1)}>
              <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
                <div style={icBox(h.ac, 32)}>
                  <Ic n="user" s={14} c={h.ac} />
                </div>
                <div>
                  <div style={{ ...type.sm, color: h.tx, fontWeight: 500 }}>{t.name}</div>
                  <div style={{ ...type.xs, color: h.tt }}>{t.phone}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>}

    {/* Service Tab */}
    {tab === "service" && <div>
      {(!org.servicePersons || org.servicePersons.length === 0) ? (
        <div style={{ ...crd(h), textAlign: "center", padding: sp.xxxl }}>
          <Ic n="wrench" s={24} c={h.tt} />
          <div style={{ ...type.sm, color: h.tt, marginTop: sp.sm }}>No service persons</div>
        </div>
      ) : (
        <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
          {org.servicePersons.map((s, i) => (
            <div key={s.id} style={listRow(h, i === org.servicePersons.length - 1)}>
              <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
                <div style={icBox(h.wn, 32)}>
                  <Ic n="wrench" s={14} c={h.wn} />
                </div>
                <div>
                  <div style={{ ...type.sm, color: h.tx, fontWeight: 500 }}>{s.name}</div>
                  <div style={{ ...type.xs, color: h.tt }}>{s.phone} {s.service_role ? `\u00B7 ${s.service_role}` : ""}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>}

    {/* Billing Tab */}
    {tab === "billing" && <div>
      {(!org.billing || org.billing.length === 0) ? (
        <div style={{ ...crd(h), textAlign: "center", padding: sp.xxxl }}>
          <Ic n="credit-card" s={24} c={h.tt} />
          <div style={{ ...type.sm, color: h.tt, marginTop: sp.sm }}>No billing records</div>
        </div>
      ) : (
        <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
          {org.billing.map((b, i) => {
            const ok = b.status === "captured" || b.status === "verified";
            const fail = b.status === "failed";
            return <div key={b.id} style={listRow(h, i === org.billing.length - 1)}>
              <div>
                <div style={{ ...type.sm, color: h.tx, fontWeight: 500 }}>{"\u20B9"}{b.amount} {b.plan_type ? <span style={{ ...type.xs, color: h.ts, fontWeight: 400 }}>· {b.plan_type}</span> : ""}</div>
                <div style={{ ...type.xs, color: h.tt }}>{b.created_at ? new Date(b.created_at).toLocaleDateString() : ""}</div>
              </div>
              <span style={bdg(
                ok ? `${h.gn}20` : fail ? `${h.rd}20` : `${h.wn}20`,
                ok ? h.gn : fail ? h.rd : h.wn
              )}>{ok ? "Paid" : b.status || "pending"}</span>
            </div>;
          })}
        </div>
      )}
    </div>}

    {/* Audit Tab */}
    {tab === "audit" && <div>
      {(!org.auditLogs || org.auditLogs.length === 0) ? (
        <div style={{ ...crd(h), textAlign: "center", padding: sp.xxxl }}>
          <Ic n="file-text" s={24} c={h.tt} />
          <div style={{ ...type.sm, color: h.tt, marginTop: sp.sm }}>No audit logs</div>
        </div>
      ) : (
        <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
          {org.auditLogs.map((log, i) => (
            <div key={log.id} style={{ ...listRow(h, i === org.auditLogs.length - 1), flexDirection: "column", alignItems: "flex-start" }}>
              <div style={{ display: "flex", alignItems: "center", gap: sp.sm, width: "100%" }}>
                <span style={bdg(`${h.ac}20`, h.ac)}>{log.action}</span>
                <span style={{ ...type.xs, color: h.tt, marginLeft: "auto" }}>
                  {log.created_at ? new Date(log.created_at).toLocaleString() : ""}
                </span>
              </div>
              <div style={{ ...type.xs, color: h.ts, marginTop: sp.xs }}>
                {log.actor_role} {"\u00B7"} {log.entity_type} {"\u00B7"} {typeof log.details === "string" ? log.details : JSON.stringify(log.details || {})}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>}
  </div>;
}
